import React, { useEffect, useRef, useState } from 'react';
import './create-package.styles.scss';
import { useNavigate, useParams } from 'react-router-dom';
import ManageContentIconCropped from '@/components/MANAGE-GAME/create-elements/blocks/icon.component';
import { UseCloudFunction } from '@/utils/firebase.utils';
import { ModalAlert } from '@/components/modal/modal.component';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import { useSelector } from 'react-redux';

const ACCESS_LEVELS = [
  { value: 0, label: '0 — Basic' },
  { value: 1, label: '1 — Pro' },
  { value: 2, label: '2 — Premium' },
  { value: 3, label: '3 — Enterprise' },
];

const defaultForm = {
  access: 0,
  description: '',
  free: true,
  icon: null,
  name: '',
  type: 'package',
};

const CreatePackage = ({
  packageList
}) => {

  const userProfile = useSelector(state => state.userData.userProfile);
  const [formData, setFormData] = useState({ ...defaultForm });
  const [alertMessage, setAlertMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const { packageId } = useParams();
  const approveRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!packageList || !packageId) return;
    const pkg = packageList[packageId];
    if (!pkg) {
      setAlertMessage('Package not found.');
      approveRef.current = () => {
        approveRef.current = null;
        navigate(-1);
      }
    } else {
      setFormData({...pkg});
    }
  }, [packageId, packageList])

  function saveData(key, value) {
    setFormData(prev => ({ ...prev, [key]: value }));
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    saveData(name, type === 'checkbox' ? checked : value);
  }

  function validate() {
    if (!formData.name.trim()) return 'Package name is required.';
    if (!formData.icon) return 'An icon is required.';
    if (!formData.description.trim()) return 'Description is required.';
    return null;
  }

  async function handleSubmit() {
    const error = validate();
    if (error) {
      setAlertMessage(error);
      return;
    }
    setLoading(true);
    let payload = {...formData};
    if (!payload.id) {
      const now = Date.now();
      const id = `${now}_contentPackage`;
      payload = {
        ...payload,
        ...{
          id: id,
          access: Number(formData.access),
          zz_elements: {},
        }
      }
    };
    const res = await UseCloudFunction(
      'saveDocToFirestore', 
      { 
        'path': `contentLibrary/${payload.id}`,
        'docData': payload,
        'createTimestamp': true,
        'appId': userProfile.appId,
      }
    );
    setLoading(false);
    if (res?.error) {
      setAlertMessage(`Something went wrong: ${res.error}`);
      return;
    }
    setAlertMessage('Package created successfully!');
    approveRef.current = () => {
      approveRef.current = null;
      navigate(-1);
    };
  }

  return (
    <div className='create-package'>
      <div className='g-card create-package-content'>
        <BackButton cancel={() => navigate(-1)} />
        <div className='g-space-1'></div>
        <div className='section-title'>Create Content Package</div>
        <hr />
        <div className='create-package-form'>

          <div className='field'>
            <label>Package Name <span className='required'>*</span></label>
            <input
              type='text'
              name='name'
              value={formData.name}
              onChange={handleChange}
              placeholder='Content Name'
              maxLength={80} />
          </div>

          <div className='field'>
            <label>Description <span className='required'>*</span></label>
            <textarea
              name='description'
              value={formData.description}
              onChange={handleChange}
              placeholder='Describe this content package...'
              rows={4} />
          </div>

          <div className='two-col'>
            <div className='field'>
              <label>Access Level</label>
              <select
                name='access'
                value={formData.access}
                onChange={handleChange}>
                {ACCESS_LEVELS.map(lvl => (
                  <option key={lvl.value} value={lvl.value}>{lvl.label}</option>
                ))}
              </select>
            </div>

            <div className='field checkbox-field'>
              <label>Free</label>
              <div className='checkbox-row'>
                <input
                  type='checkbox'
                  name='free'
                  id='free'
                  checked={formData.free}
                  onChange={handleChange} />
                <label htmlFor='free' className='checkbox-label'>
                  This package is free
                </label>
              </div>
            </div>
          </div>

          <ManageContentIconCropped
            type='package'
            formData={formData}
            saveData={saveData} />

          {/* <div className='field meta'>
            <span>Type: <b>package</b></span>
            <span className='g-space-1'></span>
            <span className='meta'>ID and timestamps will be generated on save.</span>
          </div> */}

          <div className='g-space-1'></div>
          <div className='form-buttons'>
            <button
              type='button'
              className='g-button'
              onClick={() => navigate(-1)}>
              Cancel
            </button>
            <button
              className='g-button primary'
              onClick={() => handleSubmit()}>
              Save Package
            </button>
          </div>

        </div>
      </div>

      <ModalAlert
        show={!!alertMessage}
        message={alertMessage}
        cancel={() => setAlertMessage(null)}
        onApprove={approveRef.current} />
      <FullPageLoader show={loading} />
    </div>
  );
};

export default CreatePackage;
